import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, CreditCard, Loader2, Tag, Ticket, X } from 'lucide-react';
import { toast } from 'sonner';
import RyftPay3DSDialog from './RyftPay3DSDialog';
import { apiGet, apiPost } from '../api';
import { recordBreadcrumb, reportError } from '../lib/sentry';
import { formatApiError } from '../lib/apiError';

/**
 * Shared ClassPass purchase dialog used by both `<StudioDetailPage />` and
 * `<MembershipsPage />`. Encapsulates:
 *   1. Pass-info summary card
 *   2. Optional voucher code entry + `/api/vouchers/validate`
 *   3. POST `/api/user-class-passes` (with voucher code if applied)
 *   4. RyftPay 3DS card-form flow (via `<RyftPay3DSDialog />`) when paid
 *   5. POST `/api/user-class-passes/confirm-3ds` on Approved/Captured
 *
 * The 3DS SDK form renders the customer's saved cards as one-click chips, so
 * this dialog no longer needs a separate saved-card picker / "add card" step.
 *
 * Props:
 *   pass            – the ClassPass to purchase (or `null` to keep dialog closed).
 *                     Must include: id, studio_id, name, description, activity_type,
 *                     price, passes_count, validity_days.
 *   studio          – optional studio doc (used only for the refund-notice copy).
 *   onClose         – called when user cancels or closes the dialog.
 *   onPurchased     – success callback (caller refreshes its own data).
 */
export const ClassPassPurchaseDialog = ({ pass, studio, onClose, onPurchased }) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherValidation, setVoucherValidation] = useState(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const [publicKey, setPublicKey] = useState(null);
  const [show3DS, setShow3DS] = useState(false);
  const [threeDSData, setThreeDSData] = useState(null);

  // Reset state + lazy-load the RyftPay public key whenever a new pass is opened
  useEffect(() => {
    if (!pass) return;
    setVoucherCode('');
    setVoucherValidation(null);
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiGet('/api/payments/config');
        if (!cancelled && cfg?.public_key) setPublicKey(cfg.public_key);
      } catch (e) {
        console.warn('ClassPassPurchaseDialog: failed to load payments/config', e);
      }
    })();
    return () => { cancelled = true; };
  }, [pass]);

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim() || !pass) {
      setVoucherValidation(null);
      return;
    }
    setValidatingVoucher(true);
    try {
      const data = await apiPost('/api/vouchers/validate', {
        body: {
          code: voucherCode,
          studio_id: pass.studio_id,
          amount: pass.price,
          purchase_type: 'classpass',
        },
      });
      setVoucherValidation(data);
      if (!data.valid) toast.error(data.message || 'Invalid voucher');
    } catch (_e) {
      setVoucherValidation({ valid: false, message: 'Invalid voucher code' });
      toast.error('Invalid voucher code');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const clearVoucher = () => {
    setVoucherCode('');
    setVoucherValidation(null);
  };

  const closeAll = () => {
    setShow3DS(false);
    setThreeDSData(null);
    setVoucherCode('');
    setVoucherValidation(null);
    onClose?.();
  };

  const handlePurchase = async () => {
    if (!pass) return;
    setPurchasing(true);
    recordBreadcrumb('purchase', 'User initiated ClassPass purchase', {
      class_pass_id: pass.id,
      studio_id: pass.studio_id,
      class_pass_name: pass.name,
      price: pass.price,
      voucher_code: voucherValidation?.valid ? voucherCode : null,
    });
    try {
      const requestData = {
        class_pass_id: pass.id,
        studio_id: pass.studio_id,
      };
      if (voucherValidation?.valid) requestData.voucher_code = voucherCode;

      const data = await apiPost('/api/user-class-passes', { body: requestData });

      // Free / fully-discounted — completed immediately
      if (data.status === 'completed') {
        toast.success('ClassPass purchased successfully!');
        onPurchased?.();
        closeAll();
        return;
      }

      // Paid — open 3DS dialog with SDK card form
      if (data.status === 'requires_action' && data.client_secret) {
        let customerPaymentMethods = [];
        try {
          customerPaymentMethods = await apiGet('/api/payments/customer/methods');
        } catch (_) { /* fall through to plain card-entry */ }

        setThreeDSData({
          clientSecret: data.client_secret,
          pendingPurchaseId: data.pending_purchase_id,
          accountId: data.account_id,
          redirectUrl: data.redirect_url,
          // Iter-46ar: memberships always require saved card; one-off passes don't.
          requireSaveCard: !!data.require_save_card,
          amountLabel: `€${Number(data.amount).toFixed(2)} — ${data.class_pass_name}`,
          customerPaymentMethods,
        });
        setShow3DS(true);
      }
    } catch (e) {
      reportError(e, {
        flow: 'class_pass_purchase',
        action: 'initiate',
        extra: {
          class_pass_id: pass?.id,
          studio_id: pass?.studio_id,
          has_voucher: !!voucherValidation?.valid,
        },
      });
      toast.error(formatApiError(e, 'Failed to purchase ClassPass'));
    } finally {
      setPurchasing(false);
    }
  };

  const handle3DSSuccess = async (result) => {
    recordBreadcrumb('purchase', 'User completing ClassPass 3DS confirmation', {
      pending_purchase_id: threeDSData?.pendingPurchaseId,
    });
    try {
      // Iter-44: if the user ticked the SDK's save-card checkbox, mirror the
      // new pmt to our local DB so it appears in /payment-methods.
      const sessionId = result?.paymentSession?.id;
      if (sessionId) {
        apiPost('/api/payment-methods/sync-from-session', { body: { payment_session_id: sessionId } })
          .catch((err) => console.warn('save-card sync skipped:', err?.message || err));
      }
      await apiPost('/api/user-class-passes/confirm-3ds', {
        query: { pending_purchase_id: threeDSData.pendingPurchaseId },
      });
      toast.success('ClassPass purchased successfully!');
      onPurchased?.();
      closeAll();
    } catch (e) {
      reportError(e, {
        flow: 'class_pass_purchase',
        action: 'confirm_3ds',
        extra: { pending_purchase_id: threeDSData?.pendingPurchaseId },
      });
      toast.error(formatApiError(e, 'Failed to confirm purchase'));
    }
  };

  if (!pass) return null;

  const subtotal = Number(pass.price) || 0;
  const discount = voucherValidation?.valid ? Number(voucherValidation.discount_amount) || 0 : 0;
  const total = voucherValidation?.valid ? Number(voucherValidation.final_amount) || 0 : subtotal;
  const refundNoticeHours = studio?.cancellation_notice_hours || 24;

  return (
    <>
      <Dialog open={!show3DS && !!pass} onOpenChange={(o) => !o && closeAll()}>
        <DialogContent className="sm:max-w-md" data-testid="classpass-purchase-dialog">
          <DialogHeader>
            <DialogTitle>Purchase ClassPass</DialogTitle>
            <DialogDescription>
              {pass.name}
              {pass.studio_name ? ` from ${pass.studio_name}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-5 h-5 text-primary" />
                <h4 className="font-semibold">{pass.name}</h4>
              </div>
              {pass.description && (
                <p className="text-sm text-muted-foreground mb-3">{pass.description}</p>
              )}
              <ul className="text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {pass.passes_count} classes
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  Valid for {pass.validity_days === 0 ? 'unlimited time' : `${pass.validity_days} days`}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  Activity type: {pass.activity_type}
                </li>
              </ul>
            </div>

            {/* Voucher Code */}
            <div className="space-y-2">
              <Label htmlFor="cppd-voucher" className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Have a voucher code?
              </Label>
              <div className="flex gap-2">
                <Input
                  id="cppd-voucher"
                  placeholder="Enter code"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="flex-1"
                  data-testid="voucher-code-input"
                  disabled={voucherValidation?.valid}
                />
                {voucherValidation?.valid ? (
                  <Button variant="outline" size="sm" onClick={clearVoucher} data-testid="clear-voucher-btn">
                    <X className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleValidateVoucher}
                    disabled={!voucherCode || validatingVoucher}
                    data-testid="apply-voucher-btn"
                  >
                    {validatingVoucher ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                )}
              </div>
              {voucherValidation && (
                <p className={`text-sm ${voucherValidation.valid ? 'text-emerald-600' : 'text-destructive'}`}>
                  {voucherValidation.message}
                </p>
              )}
            </div>

            {/* Payment Notice (saved-card chips appear inside the RyftPay SDK form) */}
            {subtotal > 0 && (
              <div className="space-y-2 p-4 rounded-lg border bg-muted/30">
                <Label className="text-base font-medium flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payment
                </Label>
                <p className="text-sm text-muted-foreground">
                  You'll enter your card details on the next step. Saved cards (if any) appear as one-click options inside the secure form. Your bank may require 3D Secure verification.
                </p>
              </div>
            )}

            {/* Price Summary */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>€{subtotal.toFixed(2)}</span>
              </div>
              {voucherValidation?.valid && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>
                    Discount (
                    {voucherValidation.discount_type === 'percentage'
                      ? `${voucherValidation.discount_value}%`
                      : 'fixed'}
                    )
                  </span>
                  <span data-testid="voucher-discount-amount">-€{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                <span>Total</span>
                <span data-testid="classpass-total-amount">€{total.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              ClassPass credits can be refunded if you cancel with {refundNoticeHours}h+ notice.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeAll} disabled={purchasing}>
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={purchasing}
              data-testid="confirm-classpass-btn"
            >
              {purchasing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : total > 0 ? (
                'Continue to Payment'
              ) : (
                'Purchase ClassPass'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3DS card-form dialog (only mounted while paying) */}
      <RyftPay3DSDialog
        open={show3DS}
        onClose={() => {
          setShow3DS(false);
          setThreeDSData(null);
          toast.info('Payment cancelled');
        }}
        publicKey={publicKey}
        clientSecret={threeDSData?.clientSecret}
        accountId={threeDSData?.accountId}
        redirectUrl={threeDSData?.redirectUrl}
        amountLabel={threeDSData?.amountLabel}
        customerPaymentMethods={threeDSData?.customerPaymentMethods}
        requireSaveCard={threeDSData?.requireSaveCard}
        onSuccess={handle3DSSuccess}
      />
    </>
  );
};

export default ClassPassPurchaseDialog;
